/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  IconObjectFullWidth, IconObjectLeft, IconObjectRight, IconObjectCenter, logWarning,
} from 'ckeditor5';

const defaultOptions = {
  full: {
    name: 'full',
    title: 'Full Size Block',
    icon: IconObjectFullWidth,
    isDefault: true,
  },
  side: {
    name: 'side',
    title: 'Side Block',
    icon: IconObjectRight,
    className: 'block-alignment-side',
  },
  alignLeft: {
    name: 'alignLeft',
    title: 'Left Aligned Block',
    icon: IconObjectLeft,
    className: 'block-alignment-left',
  },
  alignCenter: {
    name: 'alignCenter',
    title: 'Centered Block',
    icon: IconObjectCenter,
    className: 'block-alignment-center',
  },
  alignRight: {
    name: 'alignRight',
    title: 'Right Aligned Block',
    icon: IconObjectRight,
    className: 'block-alignment-right',
  },
  imageAlignRight: {
    name: 'imageAlignRight',
    title: 'Right Aligned Block',
    icon: IconObjectRight,
    className: 'image-alignment-right',
  },
  imageAlignLeft: {
    name: 'imageAlignLeft',
    title: 'Left Aligned Block',
    icon: IconObjectLeft,
    className: 'image-alignment-left',
  },
};

const defaultIcons = {
  full: IconObjectFullWidth,
  left: IconObjectLeft,
  right: IconObjectRight,
  center: IconObjectCenter,
};

/**
 * Normalizes an option being passed.
 *
 * @param option alignment option object.
 */
function normalizeOption(option) {
  if (typeof option === 'string') {
    const optionName = option;

    // If it's one of the defaults, just use it.
    if (defaultOptions[optionName]) {
      option = { ...defaultOptions[optionName] };
    }
    // If it's just a name but none of the defaults, warn because probably it's a mistake.
    else {
      logWarning(
        'block-alignment-not-found: There is no such block alignment of given name.',
        { name: optionName },
      );
      option = {
        name: optionName,
      };
    }
  } else if (defaultOptions[option.name]) {
    const defaultOption = defaultOptions[option.name];
    const extendedOption = { ...option };

    // eslint-disable-next-line no-restricted-syntax
    for (const prop in defaultOption) {
      if (!option.hasOwnProperty(prop)) {
        extendedOption[prop] = defaultOption[prop];
      }
    }

    option = extendedOption;
  }

  // If an icon is defined as a string and correspond with a name
  // in default icons, use the default icon provided by the plugin.
  if (typeof option.icon === 'string' && defaultIcons[option.icon]) {
    option.icon = defaultIcons[option.icon];
  }

  return option;
}

/**
 * Return a map of the alignment options.
 *
 * @param configuredOptions configured alignment options.
 */
export function normalizeAlignmentOptions(configuredOptions = []) {
  return configuredOptions.map(normalizeOption);
}
